// src/components/appSettings/Contact/ContactInfoForm/ContactInfoForm.jsx
import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchContactInfoByIdThunk } from "../../../../store/thunks/appSettingsThunks";
import { useContactForm } from "./contactFormHooks";
import Button from "../../../../components/common/Button";
import toast from "react-hot-toast";
import {
  ContactBasicInfoSection,
  ContactDetailsSection,
  AddressSection,
  BusinessHoursSection,
  SocialMediaSection,
  SupportChannelsSection,
  ServicesSection,
  ImagesSection,
  CertificationsSection,
} from "./ContactFormSections";

const ContactInfoForm = ({ mode = "add" }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { selectedContactInfo, loading } = useSelector(
    (state) => state.appSettings
  );
  const { branches } = useSelector((state) => state.branch);

  // Use custom form hook
  const {
    formData,
    errors,
    submitting,
    imagePreviews,
    handleChange,
    handleArrayChange,
    handleBusinessHoursChange,
    handleBreakTimeChange,
    handleImageChange,
    removeImage,
    removeInteriorImage,
    handleCertificationChange,
    addCertification,
    removeCertification,
    handleCertificationImageChange,
    removeCertificationImage,
    handleSubmit,
    validateForm,
  } = useContactForm(selectedContactInfo, mode);

  // Load contact info for edit mode
  useEffect(() => {
    if (mode === "edit" && id) {
      dispatch(fetchContactInfoByIdThunk(id));
    }
  }, [mode, id, dispatch]);

  // Handle form submission
  const onSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    await handleSubmit();
  };

  if (mode === "edit" && loading && !selectedContactInfo) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-6 pb-20">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {mode === "add"
                ? "Add Contact Information"
                : "Edit Contact Information"}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {mode === "add"
                ? "Add a new store location and contact details"
                : `Editing: ${selectedContactInfo?.storeName || "Contact"}`}
            </p>
          </div>
        </div>
      </div>

      {/* Basic Information */}
      <ContactBasicInfoSection
        formData={formData}
        errors={errors}
        branches={branches}
        onChange={handleChange}
      />

      {/* Contact Details */}
      <ContactDetailsSection
        formData={formData}
        errors={errors}
        onChange={handleChange}
      />

      {/* Address */}
      <AddressSection
        formData={formData}
        errors={errors}
        onChange={handleChange}
      />

      {/* Business Hours */}
      <BusinessHoursSection
        formData={formData}
        errors={errors}
        onChange={handleChange}
        onBusinessHoursChange={handleBusinessHoursChange}
        onBreakTimeChange={handleBreakTimeChange}
      />

      {/* Social Media */}
      <SocialMediaSection
        formData={formData}
        errors={errors}
        onChange={handleChange}
      />

      {/* Support Channels */}
      <SupportChannelsSection
        formData={formData}
        errors={errors}
        onChange={handleChange}
      />

      {/* Services */}
      <ServicesSection
        formData={formData}
        errors={errors}
        onChange={handleChange}
      />

      {/* Images */}
      <ImagesSection
        imagePreviews={imagePreviews}
        onImageChange={handleImageChange}
        onRemoveImage={removeImage}
        onRemoveInteriorImage={removeInteriorImage}
      />

      {/* Certifications */}
      <CertificationsSection
        formData={formData}
        onCertificationChange={handleCertificationChange}
        onAddCertification={addCertification}
        onRemoveCertification={removeCertification}
        onCertificationImageChange={handleCertificationImageChange}
        onRemoveCertificationImage={removeCertificationImage}
      />

      {/* Form Actions */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 z-10">
        <div className="container mx-auto flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/app-settings/contact")}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button type="submit" loading={submitting} disabled={submitting}>
            {submitting
              ? "Saving..."
              : mode === "add"
              ? "Create Contact Info"
              : "Update Contact Info"}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default ContactInfoForm;
